import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useCallback, useMemo } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { axisClasses, barClasses, barLabelClasses, BarPlot, ChartsContainer, ChartsGrid, ChartsXAxis, ChartsYAxis, LinePlot, MarkElement, MarkElementProps, MarkPlot } from "@mui/x-charts";
import { BarProps } from "@mui/x-charts/BarChart/AnimatedBarElement";
import { ControleEstatisticas } from "../../../../domain/models/controle_estatisticas";

/**
 * CustomBarLabelElement
 */

interface CustomBarProps extends BarProps {
  colors: string[];
}

const CustomBar = ({
  id,
  dataIndex,
  colors,
  ownerState,
  skipAnimation,
  xOrigin,
  yOrigin,
  ...props
}: CustomBarProps) => {
  return (
    <rect {...props} color={colors[dataIndex]} fill={colors[dataIndex]} />
  );
}

/**
 * CustomMarkWithLabel
 */

interface CustomMarkElementProps extends MarkElementProps {
  size: number;
  dataset: any[];
};

export const CustomMarkElement = (props: CustomMarkElementProps) => {
  const { x, y, size, dataIndex, dataset } = props;

  const value = dataset[dataIndex].meta;

  return (
    <g>
      <MarkElement
        {...props}
      />

      <text
        x={x}
        y={Number(y) - (size / 2) - 7}
        fill='white'
        textAnchor="middle"
        style={{
          fontSize: '18px',
          paintOrder: 'stroke',
          stroke: '#000000',
          strokeWidth: '2px',
          strokeLinecap: 'butt',
          strokeLinejoin: 'miter',
          fontWeight: 800,
        }}>
        {value}
      </text>
    </g>
  );
};

/**
 * InspecaoMaquinaEquipGraficoModal
 */

type Props = {
  open: boolean;
  loading: boolean;
  metaAprovacao: number;
  estatisticasControle: ControleEstatisticas | null;
  onClose: () => void;
};

export const InspecaoMaquinaEquipGraficoModal = ({
  open,
  loading,
  metaAprovacao,
  estatisticasControle,
  onClose,
}: Props) => {
  const maxValue = useMemo(() => {
    if (!estatisticasControle) {
      return 0;
    }
    let total = 0;
    if (estatisticasControle && estatisticasControle.totalA) total += estatisticasControle.totalA;
    if (estatisticasControle && estatisticasControle.totalP) total += estatisticasControle.totalP;
    if (estatisticasControle && estatisticasControle.totalC) total += estatisticasControle.totalC;
    if (estatisticasControle && estatisticasControle.totalPA) total += estatisticasControle.totalPA;
    if (estatisticasControle && estatisticasControle.totalCA) total += estatisticasControle.totalCA;
    return total * 1.1;
  }, [
    estatisticasControle,
  ]);

  const dataset = useMemo(() => {
    if (estatisticasControle === null) {
      return [];
    }
    if (estatisticasControle.totalNA === null && estatisticasControle.totalA === null &&
      estatisticasControle.totalP === null && estatisticasControle.totalC === null &&
      estatisticasControle.totalPA === null && estatisticasControle.totalCA === null
    ) {
      return [];
    }

    let total = 0;
    if (estatisticasControle && estatisticasControle.totalA) total += estatisticasControle.totalA;
    if (estatisticasControle && estatisticasControle.totalP) total += estatisticasControle.totalP;
    if (estatisticasControle && estatisticasControle.totalC) total += estatisticasControle.totalC;
    if (estatisticasControle && estatisticasControle.totalPA) total += estatisticasControle.totalPA;
    if (estatisticasControle && estatisticasControle.totalCA) total += estatisticasControle.totalCA;

    const taxaAprovacao = metaAprovacao / 100;
    const meta = taxaAprovacao && taxaAprovacao > 0 ? total * taxaAprovacao : 0;

    return [
      {
        meta: Math.round((meta + Number.EPSILON) * 100) / 100,
        quantidade: estatisticasControle.totalA ?? 0,
        period: "Aprovações (A)",
      },
      {
        meta: Math.round((meta + Number.EPSILON) * 100) / 100,
        quantidade: estatisticasControle.totalP ?? 0,
        period: "Prevenções (P)"
      },
      {
        meta: Math.round((meta + Number.EPSILON) * 100) / 100,
        quantidade: estatisticasControle.totalC ?? 0,
        period: "Correções (C)"
      },
      {
        meta: Math.round((meta + Number.EPSILON) * 100) / 100,
        quantidade: estatisticasControle.totalPA ?? 0,
        period: "Prevenções Aprov. (PA)"
      },
      {
        meta: Math.round((meta + Number.EPSILON) * 100) / 100,
        quantidade: estatisticasControle.totalCA ?? 0,
        period: "Correções Aprov. (CA)"
      },
      {
        meta: Math.round((meta + Number.EPSILON) * 100) / 100,
        quantidade: estatisticasControle.totalNA ?? 0,
        period: "Não se aplica (NA)"
      }
    ];
  }, [
    metaAprovacao,
    estatisticasControle,
  ]);

  const colors = useMemo(() => {
    return ['#26c281', '#ffff00', '#fb3113', '#ff8000', '#8ea3df', '#996056'];
  }, [
  ]);

  const xAxis = useMemo((): any => {
    return [
      { scaleType: "band", dataKey: 'period' },
    ];
  }, [
  ]);

  const series = useMemo((): any => {
    return [
      { id: 1, type: 'bar', dataKey: 'quantidade', barLabel: 'value' },
      { id: 2, type: 'line', dataKey: 'meta', barLabel: 'value' },
    ];
  }, [
  ]);

  const chartSetting = useMemo(() => ({
    sx: {
      [`& .${axisClasses.directionY} .${axisClasses.label}`]: {
        transform: 'translateX(-10px)',
      },
      [`& .${axisClasses.directionX} .${axisClasses.label}`]: {
        transform: 'translateY(-10px)',
      },
    },
  }), [
  ]);

  const handleClose = useCallback(() => {
    if (!loading) onClose();
  }, [
    loading,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "70rem",
          },
        },
      }}>
      <DialogTitle>
        Gráfico Resumo
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={loading} message="Carregando...">
          <div className="w-full h-[30rem]">
            <ChartsContainer
              dataset={dataset}
              xAxis={xAxis}
              yAxis={[{ min: 0, max: maxValue }]}
              series={series}
              {...chartSetting}
              sx={{
                [`& .${barClasses.root} .${barClasses.seriesLabels} .${barLabelClasses.root}`]: {
                  fill: 'white',
                  fontSize: '18px',
                  paintOrder: 'stroke',
                  stroke: '#000000',
                  strokeWidth: '2px',
                  strokeLinecap: 'butt',
                  strokeLinejoin: 'miter',
                  fontWeight: 800,
                },
                '& .MuiMarkElement-root': {
                  fill: '#a5e900',
                  stroke: '#00c000',
                },
              }}>

              <ChartsGrid horizontal={true} />

              <BarPlot
                slots={{ bar: CustomBar as any }}
                slotProps={{ bar: { colors } as any }}
              />

              <LinePlot
                slots={{
                  line: ({ skipAnimation, ownerState, ...props }) => <path {...props} stroke="#00c000" />
                }}
              />

              <MarkPlot
                slots={{
                  mark: props => (
                    <CustomMarkElement
                      size={8}
                      dataset={dataset}
                      {...props}
                    />
                  )
                }}
              />

              <ChartsXAxis />
              <ChartsYAxis />
            </ChartsContainer>
          </div>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex p-4 gap-4">
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Fechar
              </h1>
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog >
  );
}